import { useEffect, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import type { WorkspaceMessageAttachment } from './types';

function fileSize(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${Math.round(kilobytes)} KB`;
  return `${(kilobytes / 1024).toFixed(1)} MB`;
}

function useAttachmentUrl(attachment: WorkspaceMessageAttachment, loadImmediately: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!loadImmediately) return;
    let active = true;
    let objectUrl: string | null = null;
    sentinelWorkspaceService
      .getAttachmentBlob(attachment.ticketId, attachment.messageId, attachment.index)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.index, attachment.messageId, attachment.ticketId, loadImmediately]);

  return { url, failed };
}

async function downloadAttachment(attachment: WorkspaceMessageAttachment): Promise<void> {
  const blob = await sentinelWorkspaceService.getAttachmentBlob(
    attachment.ticketId,
    attachment.messageId,
    attachment.index,
  );
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = attachment.fileName;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

function AttachmentItem({ attachment }: { attachment: WorkspaceMessageAttachment }) {
  const previewable = attachment.fileType !== 'file';
  const { url, failed } = useAttachmentUrl(attachment, previewable);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const size = fileSize(attachment.fileSize);

  if (failed || downloadFailed) {
    return (
      // Same cap and truncation as the download button: a provider can send a CDN URL as the
      // file name, and an unbroken 500-character token would otherwise stretch the bubble.
      <div className="max-w-[320px] rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-[11px] text-gray-700">
        <p className="truncate font-medium text-text-body" title={attachment.fileName}>
          {attachment.fileName}
        </p>
        <p>File unavailable</p>
      </div>
    );
  }

  if (attachment.fileType === 'image') {
    return url ? (
      // The image speaks for itself; its name stays on hover and for screen readers.
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={attachment.fileName}
        className="block max-w-[280px] overflow-hidden rounded-lg border border-gray-300"
      >
        <img src={url} alt={attachment.fileName} className="max-h-56 w-full object-contain" />
      </a>
    ) : (
      <div className="h-24 w-48 animate-pulse rounded-lg bg-gray-100" aria-label={`Loading ${attachment.fileName}`} />
    );
  }

  if (attachment.fileType === 'video') {
    return url ? <video src={url} controls aria-label={attachment.fileName} className="max-h-56 max-w-[320px] rounded-lg" /> : null;
  }

  if (attachment.fileType === 'audio') {
    return url ? <audio src={url} controls aria-label={attachment.fileName} className="max-w-full" /> : null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDownloadFailed(false);
        void downloadAttachment(attachment).catch(() => setDownloadFailed(true));
      }}
      className="flex max-w-[320px] items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-left hover:bg-gray-100"
      aria-label={`Download ${attachment.fileName}`}
    >
      <FileText className="size-5 shrink-0 text-gray-700" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[11px] font-medium text-text-body">{attachment.fileName}</span>
        {size && <span className="block text-[10px] text-gray-700">{size}</span>}
      </span>
      <Download className="size-4 shrink-0 text-gray-700" />
    </button>
  );
}

export function MessageAttachments({ attachments }: { attachments?: WorkspaceMessageAttachment[] }) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {attachments.map((attachment) => (
        <AttachmentItem key={`${attachment.messageId}-${attachment.index}`} attachment={attachment} />
      ))}
    </div>
  );
}
